'use client';

import { useLoginStatusStore } from '@/stores/LoginStatusStore';
import { useSettingsStore } from '@/stores/SettingsStore';
import { Button, Tooltip, Menu, MenuItem, ListItemText, Typography, IconButton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorIcon from '@mui/icons-material/Error';
import LoginIcon from '@mui/icons-material/Login';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import SettingsIcon from '@mui/icons-material/Settings';
import LanguageIcon from '@mui/icons-material/Language';
import PictureInPictureAltIcon from '@mui/icons-material/PictureInPictureAlt';
import { LightMode, DarkMode } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { SettingsDialog } from '@/components/settings';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import Cookies from 'js-cookie';
import { usePictureInPicture } from '@/components/pictureInPicture';
import { useTheme } from 'next-themes';

export const TopToolBar = () => {
  const t = useTranslations('TopToolBar');
  const loginStatusStore = useLoginStatusStore();
  const { openSettings, language, setLanguage } = useSettingsStore();
  const { pipWindow, openPipWindow, closePipWindow } = usePictureInPicture();
  const { theme, setTheme } = useTheme();
  const isLoggedIn = loginStatusStore.isLoggedIn;
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'error'>('idle');
  const [isLoggingIn] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);
  const [mounted, setMounted] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const browserLang = navigator.language.split('-')[0];
    if (!language && ['en', 'zh', 'ja', 'de', 'fr'].includes(browserLang)) {
      setLanguage(browserLang);
    }
  }, [language, setLanguage]);

  useEffect(() => {
    setMounted(true);
  }, []);

  const languages = [
    { code: 'zh' as const, name: t('languages.zh'), flag: <><span className="fi fi-cn"></span></> },
    { code: 'en' as const, name: t('languages.en'), flag: <><span className="fi fi-gb"></span></> },
    { code: 'fr' as const, name: t('languages.fr'), flag: <><span className="fi fi-fr"></span></> },
    { code: 'de' as const, name: t('languages.de'), flag: <><span className="fi fi-de"></span></> },
    { code: 'ja' as const, name: t('languages.ja'), flag: <><span className="fi fi-jp"></span></> },
  ];

  const handleLanguageChange = (lang: string) => {
    setLanguage(lang);
    Cookies.set('locale', lang, { expires: 365 });
    router.refresh();
    setLanguageMenuAnchor(null);
  };

  const getSyncStatusIcon = () => {
    return <>
      {syncStatus === 'syncing' && (
        <RefreshIcon
          className="animate-spin"
          style={{
            transformOrigin: 'center center',
            display: 'block'
          }}
          sx={{
            color: "var(--color-primary)"
          }}
        />
      )}
      {syncStatus === 'error' && (
        <ErrorIcon sx={{
          color: "var(--color-primary)"
        }} />
      )}
      {syncStatus === 'idle' && (
        <CloudDoneIcon sx={{
          color: "var(--color-primary)"
        }} />
      )}
    </>
  };

  const formatLastSyncTime = (time: Date | null) => {
    if (!time) return t('syncStatus.notSynced');

    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - time.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return t('syncStatus.justNow');
    if (diffInMinutes < 60) return t('syncStatus.minutesAgo', { minutes: diffInMinutes });

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return t('syncStatus.hoursAgo', { hours: diffInHours });

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return t('syncStatus.daysAgo', { days: diffInDays });

    return time.toLocaleDateString(language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTooltipTitle = () => {
    switch (syncStatus) {
      case 'syncing':
        return t('syncStatus.syncing');
      case 'error':
        return t('syncStatus.failed');
      case 'idle':
      default:
        return t('syncStatus.lastSync', { time: formatLastSyncTime(lastSyncTime) });
    }
  };

  return (
    <div className="w-full backdrop-blur-sm px-6 py-3">
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center space-x-4"></div>

        <div className="flex justify-center items-center space-x-2">
          <Tooltip title={t('selectLanguage')} placement="bottom">
            <Button
              size="small"
              sx={{
                minWidth: '32px',
                width: '32px',
                height: '32px',
                padding: 0,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--color-primary)',
              }}
              onClick={(event) => {
                setLanguageMenuAnchor(event.currentTarget);
              }}>
              <LanguageIcon />
            </Button>
          </Tooltip>

          <Menu
            anchorEl={languageMenuAnchor}
            open={Boolean(languageMenuAnchor)}
            onClose={() => setLanguageMenuAnchor(null)}
            slotProps={{
              paper: {
                sx: {
                  minWidth: 160,
                  borderRadius: '16px',
                  boxShadow: '1px'
                }
              }
            }}
          >
            {languages.map((lang) => (
              <MenuItem
                key={lang.code}
                selected={language === lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className="flex items-center space-x-2"
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'var(--color-primary-light)',
                  }
                }}
              >
                <Typography className='text-sm'>{lang.flag}</Typography>
                <ListItemText primary={lang.name} />
              </MenuItem>
            ))}
          </Menu>

          <Tooltip title={t('themeToggle')} placement="bottom">
            <IconButton
              size="small"
              onClick={() => {
                if (!mounted) return;
                setTheme(theme === 'dark' ? 'light' : 'dark');
              }}
              sx={{
                color: 'var(--color-primary)',
                '&:hover': {
                  backgroundColor: 'var(--color-primary-light)',
                },
              }}
              aria-label="Toggle theme"
            >
              {!mounted ? <LightMode /> : theme === 'dark' ? <LightMode /> : <DarkMode />}
            </IconButton>
          </Tooltip>

          <Tooltip title={t('pictureInPicture')} placement="bottom">
            <Button
              size="small"
              sx={{
                minWidth: '32px',
                width: '32px',
                height: '32px',
                padding: 0,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={() => {
                if (pipWindow) {
                  closePipWindow();
                } else {
                  openPipWindow();
                }
              }}>
              <PictureInPictureAltIcon fontSize="small" sx={{
                color: "var(--color-primary)"
              }} />
            </Button>
          </Tooltip>

          <Tooltip title={t('settings')} placement="bottom">
            <Button
              size="small"
              sx={{
                minWidth: '32px',
                width: '32px',
                height: '32px',
                padding: 0,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={openSettings}>
              <SettingsIcon fontSize="small" sx={{
                color: "var(--color-primary)"
              }} />
            </Button>
          </Tooltip>

          <Tooltip title={getTooltipTitle()} placement="bottom">
            <Button
              size="small"
              sx={{
                minWidth: '32px',
                width: '32px',
                height: '32px',
                padding: 0,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={() => {
                if (syncStatus === 'syncing') {
                  setSyncStatus('idle');
                  return;
                }
                setSyncStatus('syncing');
                setTimeout(() => {
                  setSyncStatus('idle');
                  setLastSyncTime(new Date());
                }, 2000);
              }}>
              {getSyncStatusIcon()}
            </Button>
          </Tooltip>

          <Tooltip title={t('cloudSync')} placement="bottom">
            <Button
              variant="contained"
              startIcon={<LoginIcon fontSize="small" sx={{ color: 'var(--color-login-text)' }} />}
              disabled={isLoggingIn}
              size="small"
              onClick={() => { }}
              sx={{
                boxShadow: 'none',
                textTransform: 'none',
                borderRadius: '16px',
                backgroundColor: isLoggedIn ? 'var(--color-error)' : 'var(--color-primary)',
                '&:hover': {
                  backgroundColor: isLoggedIn ? 'var(--color-error)' : 'var(--color-primary-hover)',
                },
              }}
            >
              <Typography className="text-xs" sx={{ color: 'var(--color-login-text)' }}>
                {isLoggingIn ? t('loggingIn') : isLoggedIn ? t('logout') : t('login')}
              </Typography>
            </Button>
          </Tooltip>
        </div>
      </div>

      <SettingsDialog />
    </div>
  );
};
